import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { CodeNode, CodeHighlightNode } from "@lexical/code";
import {
  TableNode,
  TableCellNode,
  TableRowNode,
  INSERT_TABLE_COMMAND,
  $isTableCellNode,
  $insertTableRowAtSelection,
  $deleteTableRowAtSelection,
  $insertTableColumnAtSelection,
  $deleteTableColumnAtSelection,
  $getTableCellNodeFromLexicalNode,
  $getTableNodeFromLexicalNodeOrThrow,
} from "@lexical/table";
import { OverflowNode } from "@lexical/overflow";
import { HorizontalRuleNode, INSERT_HORIZONTAL_RULE_COMMAND } from "@lexical/react/LexicalHorizontalRuleNode";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import {
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  SELECTION_CHANGE_COMMAND,
  $createParagraphNode,
  $isElementNode,
  CAN_UNDO_COMMAND,
  CAN_REDO_COMMAND,
  ElementFormatType,
  TextFormatType,
  $insertNodes,
  $getNodeByKey,
  LexicalEditor,
} from "lexical";
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, INSERT_CHECK_LIST_COMMAND } from "@lexical/list";
import { $setBlocksType } from "@lexical/selection";
import { $createHeadingNode, $createQuoteNode, $isHeadingNode } from "@lexical/rich-text";
import { $isListNode } from "@lexical/list";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $getNearestNodeOfType } from "@lexical/utils";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./letterhead.css";
import { ImageNode, $createImageNode, FloatingImageNode, $createFloatingImageNode } from "./nodes";

// ═══════════════════════════════════════════════════════
// SVG ICONS
// ═══════════════════════════════════════════════════════
const I = {
  undo: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path fillRule="evenodd" d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.418A6 6 0 1 0 8 2v1z" />
      <path d="M8 4.466V.534a.25.25 0 0 0-.41-.192L5.23 2.308a.25.25 0 0 0 0 .384l2.36 1.966A.25.25 0 0 0 8 4.466z" />
    </svg>
  ),
  redo: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.418A6 6 0 1 1 8 2v1z" />
      <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966a.25.25 0 0 1 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
    </svg>
  ),
  bold: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M8.21 13c2.106 0 3.412-1.087 3.412-2.823 0-1.306-.984-2.283-2.324-2.386v-.055a2.176 2.176 0 0 0 1.852-2.14c0-1.51-1.162-2.46-3.014-2.46H3.843V13H8.21zM5.908 4.674h1.696c.963 0 1.517.451 1.517 1.244 0 .834-.629 1.32-1.73 1.32H5.908V4.673zm0 6.788V8.598h1.73c1.217 0 1.88.492 1.88 1.415 0 .943-.643 1.449-1.832 1.449H5.907z" />
    </svg>
  ),
  italic: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M7.991 11.674L9.53 4.455c.123-.595.246-.71 1.347-.807l.11-.52H7.211l-.11.52c1.06.096 1.128.212 1.005.807L6.57 11.674c-.123.595-.246.71-1.346.806l-.11.52h3.774l.11-.52c-1.06-.095-1.129-.211-1.006-.806z" />
    </svg>
  ),
  underline: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M5.313 3.136h-1.23V9.54c0 2.105 1.47 3.623 3.917 3.623s3.917-1.518 3.917-3.623V3.136h-1.23v6.323c0 1.49-.978 2.57-2.687 2.57-1.709 0-2.687-1.08-2.687-2.57V3.136z" />
      <path fillRule="evenodd" d="M12.5 15h-9v-1h9v1z" />
    </svg>
  ),
  strikethrough: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M6.333 5.686c0 .31.083.581.27.814H5.166a2.776 2.776 0 0 1-.099-.76c0-1.627 1.436-2.768 3.48-2.768 1.969 0 3.39 1.175 3.445 2.85h-1.23c-.11-1.08-.964-1.743-2.25-1.743-1.23 0-2.18.602-2.18 1.607zm2.194 7.478c-2.153 0-3.589-1.107-3.705-2.81h1.23c.144 1.06 1.129 1.703 2.544 1.703 1.34 0 2.31-.705 2.31-1.675 0-.827-.547-1.374-1.914-1.675L8.046 8.5H1v-1h14v1h-3.504c.468.437.675.994.675 1.697 0 1.826-1.436 2.967-3.644 2.967z" />
    </svg>
  ),
  subscript: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M2.934 3h1.5l2.568 4.182L9.57 3h1.5L7.824 8.293 11.3 13H9.77L7.002 8.822 4.234 13H2.704l3.476-4.707L2.934 3zm9.375 9v1h3.19c0-.37-.093-.715-.277-1.022a2.26 2.26 0 0 0-.75-.741c-.257-.16-.548-.3-.873-.426a24 24 0 0 0-.894-.34c-.264-.1-.48-.203-.65-.309-.169-.105-.25-.234-.25-.39 0-.176.075-.312.225-.41.15-.097.363-.146.638-.146.29 0 .522.062.695.187.173.125.262.304.268.537h1.078c-.025-.55-.22-.982-.584-1.295-.363-.312-.852-.469-1.466-.469-.59 0-1.066.147-1.428.44-.362.294-.543.68-.543 1.161 0 .384.116.704.348.96.232.256.54.461.924.614.385.153.792.302 1.223.447.195.065.377.137.546.216.169.078.306.175.41.29a.575.575 0 0 1 .158.407c0 .182-.08.327-.24.435-.16.108-.384.162-.674.162a1.12 1.12 0 0 1-.747-.262c-.198-.174-.303-.412-.313-.714z" />
    </svg>
  ),
  superscript: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M2.934 7h1.5l2.568 4.182L9.57 7h1.5L7.824 12.293 11.3 17H9.77L7.002 12.822 4.234 17H2.704l3.476-4.707L2.934 7zm9.375-3v1h3.19c0-.37-.093-.715-.277-1.022a2.26 2.26 0 0 0-.75-.741 4 4 0 0 0-.873-.426 24 24 0 0 0-.894-.34c-.264-.1-.48-.203-.65-.309-.169-.105-.25-.234-.25-.39 0-.176.075-.312.225-.41.15-.097.363-.146.638-.146.29 0 .522.062.695.187.173.125.262.304.268.537h1.078c-.025-.55-.22-.982-.584-1.295-.363-.312-.852-.469-1.466-.469-.59 0-1.066.147-1.428.44-.362.294-.543.68-.543 1.161 0 .384.116.704.348.96.232.256.54.461.924.614.385.153.792.302 1.223.447.195.065.377.137.546.216.169.078.306.175.41.29A.575.575 0 0 1 15 3.407c0 .182-.08.327-.24.435-.16.108-.384.162-.674.162a1.12 1.12 0 0 1-.747-.262c-.198-.174-.303-.412-.313-.714z" />
    </svg>
  ),
  code: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M5.854 4.854a.5.5 0 1 0-.708-.708l-3.5 3.5a.5.5 0 0 0 0 .708l3.5 3.5a.5.5 0 0 0 .708-.708L2.707 8l3.147-3.146zm4.292 0a.5.5 0 0 1 .708-.708l3.5 3.5a.5.5 0 0 1 0 .708l-3.5 3.5a.5.5 0 0 1-.708-.708L13.293 8l-3.147-3.146z" />
    </svg>
  ),
  link: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M4.715 6.542L3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1 1 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z" />
      <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243L6.586 4.672z" />
    </svg>
  ),
  alignLeft: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M2 12.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"
      />
    </svg>
  ),
  alignCenter: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M4 12.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"
      />
    </svg>
  ),
  alignRight: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M6 12.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-4-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm4-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-4-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"
      />
    </svg>
  ),
  alignJustify: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M2 12.5a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"
      />
    </svg>
  ),
  indent: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M3 8a.5.5 0 0 1 .5-.5h8.793l-2.147-2.146a.5.5 0 0 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L12.293 8.5H3.5A.5.5 0 0 1 3 8z"
      />
    </svg>
  ),
  outdent: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M13 8a.5.5 0 0 0-.5-.5H3.707l2.147-2.146a.5.5 0 1 0-.708-.708l-3 3a.5.5 0 0 0 0 .708l3 3a.5.5 0 0 0 .708-.708L3.707 8.5H12.5A.5.5 0 0 0 13 8z"
      />
    </svg>
  ),
  bulletList: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm-3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm0 4a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm0 4a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
      />
    </svg>
  ),
  numberedList: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5z"
      />
      <path d="M1.713 11.865v.29c0 .107.017.196.05.267a.455.455 0 0 0 .138.178c.056.042.137.075.208.107a.8.8 0 0 0 .26.04c.163 0 .294-.036.393-.107a.358.358 0 0 0 .149-.302.35.35 0 0 0-.063-.218.478.478 0 0 0-.17-.148 1.1 1.1 0 0 0-.245-.107L2.143 11.7a3.94 3.94 0 0 1-.388-.135 1.16 1.16 0 0 1-.32-.194.693.693 0 0 1-.217-.3A1.14 1.14 0 0 1 1.14 11c0-.15.031-.285.093-.408a.805.805 0 0 1 .257-.302.984.984 0 0 1 .39-.178 1.96 1.96 0 0 1 .488-.058c.174 0 .334.018.479.055a.993.993 0 0 1 .382.178.807.807 0 0 1 .252.31 1.1 1.1 0 0 1 .09.449h-.816a.472.472 0 0 0-.127-.35c-.092-.089-.22-.133-.384-.133a.638.638 0 0 0-.248.042.342.342 0 0 0-.155.122.355.355 0 0 0-.052.196c0 .071.017.134.052.186a.42.42 0 0 0 .15.13c.065.038.143.07.232.097l.27.079z" />
    </svg>
  ),
  checkList: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
      <path d="M10.97 4.97a.75.75 0 0 1 1.071 1.05l-3.992 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.24.24 0 0 1 .02-.022z" />
    </svg>
  ),
  quote: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-2.5a2 2 0 0 0-1.6.8L8 14.333 6.1 11.8a2 2 0 0 0-1.6-.8H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2.5a1 1 0 0 1 .8.4l1.9 2.533a1 1 0 0 0 1.6 0l1.9-2.533a1 1 0 0 1 .8-.4H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
      <path d="M7.066 4.76A1.665 1.665 0 0 0 4 5.668a1.667 1.667 0 0 0 2.561 1.406c-.131.389-.375.804-.777 1.22a.417.417 0 1 0 .6.58c1.486-1.54 1.293-3.214.682-4.112zm4 0A1.665 1.665 0 0 0 8 5.668a1.667 1.667 0 0 0 2.561 1.406c-.131.389-.375.804-.777 1.22a.417.417 0 1 0 .6.58c1.486-1.54 1.293-3.214.682-4.112z" />
    </svg>
  ),
  paragraph: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M10.5 15a.5.5 0 0 1-.5-.5V2H9v12.5a.5.5 0 0 1-1 0V9H7a4 4 0 1 1 0-8h5.5a.5.5 0 0 1 0 1H11v12.5a.5.5 0 0 1-.5.5z" />
    </svg>
  ),
  h1: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M8.637 13V3.669H7.379V7.62H2.758V3.67H1.5V13h1.258V8.728h4.62V13h1.259zm5.329 0V3.669h-1.244L10.5 5.316v1.265l2.16-1.565h.062V13h1.244z" />
    </svg>
  ),
  h2: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M7.638 13V3.669H6.38V7.62H1.759V3.67H.5V13h1.258V8.728h4.62V13h1.259zm3.022-6.733v-.048c0-.889.63-1.668 1.716-1.668.957 0 1.675.608 1.675 1.572 0 .855-.554 1.504-1.067 2.085l-3.513 3.999V13H15.5v-1.094h-4.245l2.7-2.868c.837-.924 1.42-1.727 1.42-2.845 0-1.481-1.162-2.524-2.84-2.524-1.834 0-2.925 1.222-2.925 2.693v.048h1.05z" />
    </svg>
  ),
  h3: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M7.637 13V3.669H6.379V7.62H1.758V3.67H.5V13h1.258V8.728h4.62V13h1.259zm4.958-.956c0 1.106-.89 1.753-2.087 1.753-1.217 0-2.17-.705-2.17-1.822h1.067c0 .5.424.883 1.103.883.66 0 1.126-.39 1.126-.957 0-.715-.733-1.063-1.452-.963v-.936c.734.068 1.332-.339 1.332-.965 0-.491-.392-.852-.991-.852-.6 0-1.038.374-1.038.878h-1.04c0-1.111.868-1.828 2.078-1.828 1.156 0 2.007.718 2.007 1.697 0 .679-.383 1.173-.978 1.396v.063c.703.177 1.043.742 1.043 1.353z" />
    </svg>
  ),
  fontColor: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M0 14.5a.5.5 0 0 1 .5-.5h15a.5.5 0 0 1 0 1H.5a.5.5 0 0 1-.5-.5z" />
      <path d="M6 1a1 1 0 0 0-.98.804L2.04 12h1.065l.762-3.5h4.264l.763 3.5h1.064L6.98 1.804A1 1 0 0 0 6 1zm.264 2.21l1.62 4.29H4.616l1.62-4.29h.028z" />
    </svg>
  ),
  bgColor: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M6.192 2.78c-.458-.677-.927-1.248-1.35-1.643a3 3 0 0 0-.71-.515c-.217-.104-.56-.205-.882-.02-.367.213-.427.63-.43.896-.003.304.064.664.173 1.044.196.687.556 1.528 1.035 2.402L.752 8.22c-.277.277-.269.656-.218.918.055.283.187.593.36.903.348.627.92 1.361 1.626 2.068.707.707 1.441 1.278 2.068 1.626.31.173.62.305.903.36.262.051.641.059.918-.218l5.615-5.615c.118.257.092.512.05.939-.03.292-.068.665-.073 1.176v.123h.003a1 1 0 0 0 1.993 0H14v-.057a1 1 0 0 0-.004-.117c-.055-1.25-.7-2.738-1.293-3.707a10 10 0 0 0-.25-.383L6.19 2.78h.002z" />
    </svg>
  ),
  clearFormat: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M8.086 2.207a2 2 0 0 1 2.828 0l3.879 3.879a2 2 0 0 1 0 2.828l-5.5 5.5A2 2 0 0 1 7.879 15H5.12a2 2 0 0 1-1.414-.586l-2.5-2.5a2 2 0 0 1 0-2.828l5.88-5.88zm2.121.707a1 1 0 0 0-1.414 0L4.16 7.547l5.293 5.293 4.633-4.633a1 1 0 0 0 0-1.414l-3.879-3.879zM8.746 13.547L3.453 8.254 1.914 9.793a1 1 0 0 0 0 1.414l2.5 2.5a1 1 0 0 0 .707.293H7.88a1 1 0 0 0 .707-.293l.16-.16z" />
    </svg>
  ),
  hr: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path fillRule="evenodd" d="M0 8a.5.5 0 0 1 .5-.5h15a.5.5 0 0 1 0 1H.5A.5.5 0 0 1 0 8z" />
    </svg>
  ),
  table: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm15 2h-4v3h4V4zm0 4h-4v3h4V8zm0 4h-4v3h3a1 1 0 0 0 1-1v-2zM9.5 4h-3v3h3V4zm0 4h-3v3h3V8zm0 4h-3v3h3v-3zM5.5 4H1v3h4.5V4zm0 4H1v3h4.5V8zm0 4H1v2a1 1 0 0 0 1 1h3.5v-3z" />
    </svg>
  ),
  image: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
      <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z" />
    </svg>
  ),
  pdf: (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z" />
      <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.7 19.7 0 0 0 1.062-2.227 7.27 7.27 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.95 10.95 0 0 0 .98 1.686 5.75 5.75 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a6.12 6.12 0 0 1-.911-.95 11.65 11.65 0 0 0-1.997.406 11.31 11.31 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029z" />
    </svg>
  ),
  chevron: (
    <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: 12, height: 12 }}>
      <path
        fillRule="evenodd"
        d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
      />
    </svg>
  ),
};

// ═══════════════════════════════════════════════════════
// THEME
// ═══════════════════════════════════════════════════════
const editorTheme = {
  ltr: "ltr",
  rtl: "rtl",
  paragraph: "editor-paragraph",
  quote: "editor-quote",
  heading: { h1: "editor-heading-h1", h2: "editor-heading-h2", h3: "editor-heading-h3" },
  list: {
    nested: { listitem: "editor-nested-listitem" },
    ol: "editor-list-ol",
    ul: "editor-list-ul",
    listitem: "editor-listitem",
    checklist: "editor-checklist",
  },
  text: {
    bold: "editor-text-bold",
    italic: "editor-text-italic",
    underline: "editor-text-underline",
    strikethrough: "editor-text-strikethrough",
    subscript: "editor-text-subscript",
    superscript: "editor-text-superscript",
    code: "editor-text-code",
    underlineStrikethrough: "editor-text-underline-strikethrough",
  },
  link: "editor-link",
  code: "editor-code",
  horizontalRule: "editor-hr",
  table: "editor-table",
  tableCell: "editor-tableCell",
  tableCellHeader: "editor-tableCellHeader",
  tableRow: "",
};

const FONT_FAMILIES = [
  "Arial",
  "Helvetica",
  "Georgia",
  "Times New Roman",
  "Courier New",
  "Verdana",
  "Trebuchet MS",
  "Tahoma",
  "Garamond",
  "Palatino",
];

const BLOCK_TYPES: Record<string, { label: string; icon: JSX.Element }> = {
  paragraph: { label: "Normal", icon: I.paragraph },
  h1: { label: "Heading 1", icon: I.h1 },
  h2: { label: "Heading 2", icon: I.h2 },
  h3: { label: "Heading 3", icon: I.h3 },
  bullet: { label: "Bullet List", icon: I.bulletList },
  number: { label: "Num. List", icon: I.numberedList },
  check: { label: "Check List", icon: I.checkList },
  quote: { label: "Quote", icon: I.quote },
  code: { label: "Code Block", icon: I.code },
};

// ═══════════════════════════════════════════════════════
// TOOLBAR
// ═══════════════════════════════════════════════════════
function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] = useState("paragraph");
  const [fontSize, setFontSize] = useState("15");
  const [fontFamily, setFontFamily] = useState("Arial");
  const [fontColor, setFontColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [elementFormat, setElementFormat] = useState<ElementFormatType>("left");
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [showBlockDD, setShowBlockDD] = useState(false);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [imageUrl, setImageUrl] = useState("");
  const blockBtnRef = useRef<HTMLButtonElement>(null);
  const blockDDRef = useRef<HTMLDivElement>(null);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      setIsSubscript(selection.hasFormat("subscript"));
      setIsSuperscript(selection.hasFormat("superscript"));
      setIsCode(selection.hasFormat("code"));

      const node = selection.anchor.getNode();
      const parent = node.getParent();
      const style = (node as any).getStyle?.() || "";
      const parentStyle = (parent as any)?.getStyle?.() || "";
      const all = parentStyle + ";" + style;
      const fsM = all.match(/font-size:\s*(\d+)px/);
      if (fsM) setFontSize(fsM[1]);
      const ffM = all.match(/font-family:\s*([^;]+)/);
      if (ffM) setFontFamily(ffM[1].replace(/['"]/g, "").trim());

      setIsLink(!!($isLinkNode(parent) ? parent : $isLinkNode(node) ? node : null));

      const anchorNode = selection.anchor.getNode();
      let element = anchorNode.getTopLevelElementOrThrow();
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode);
        const t = parentList ? parentList.getListType() : (element as any).getListType();
        setBlockType(t === "bullet" ? "bullet" : t === "check" ? "check" : "number");
      } else {
        const t = $isHeadingNode(element) ? element.getTag() : element.getType();
        setBlockType(t in BLOCK_TYPES ? t : "paragraph");
      }
      if ($isElementNode(element)) setElementFormat(element.getFormatType() || "left");
    }
  }, []);

  useEffect(
    () =>
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    [editor, updateToolbar],
  );
  useEffect(
    () =>
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (p) => {
          setCanUndo(p);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    [editor],
  );
  useEffect(
    () =>
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (p) => {
          setCanRedo(p);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    [editor],
  );

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (blockDDRef.current && !blockDDRef.current.contains(e.target as Node) && !blockBtnRef.current?.contains(e.target as Node))
        setShowBlockDD(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const formatBlock = (type: string) => {
    setShowBlockDD(false);
    if (type === "paragraph") {
      editor.update(() => {
        const s = $getSelection();
        if ($isRangeSelection(s)) $setBlocksType(s, () => $createParagraphNode());
      });
    } else if (type === "h1" || type === "h2" || type === "h3") {
      editor.update(() => {
        const s = $getSelection();
        if ($isRangeSelection(s)) $setBlocksType(s, () => $createHeadingNode(type as any));
      });
    } else if (type === "bullet") editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    else if (type === "number") editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    else if (type === "check") editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    else if (type === "quote") {
      editor.update(() => {
        const s = $getSelection();
        if ($isRangeSelection(s)) $setBlocksType(s, () => $createQuoteNode());
      });
    }
  };

  const applyStyle = (styles: Record<string, string>) => {
    editor.update(() => {
      const sel = $getSelection();
      if (!$isRangeSelection(sel)) return;
      sel.getNodes().forEach((node) => {
        if ((node as any).setStyle) {
          const cur = ((node as any).getStyle() || "").split(";").filter((p: string) => p.trim());
          Object.entries(styles).forEach(([k, v]) => {
            const idx = cur.findIndex((p: string) => p.trim().startsWith(k + ":"));
            if (idx >= 0) cur[idx] = `${k}: ${v}`;
            else cur.push(`${k}: ${v}`);
          });
          (node as any).setStyle(cur.join("; "));
        }
      });
    });
  };

  const setFS = (s: string) => {
    setFontSize(s);
    applyStyle({ "font-size": s + "px" });
  };
  const setFF = (f: string) => {
    setFontFamily(f);
    applyStyle({ "font-family": f });
  };
  const setFC = (c: string) => {
    setFontColor(c);
    applyStyle({ color: c });
  };
  const setBG = (c: string) => {
    setBgColor(c);
    applyStyle({ "background-color": c });
  };

  const insertLink = () => {
    if (isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      return;
    }
    const url = prompt("Enter URL:");
    if (url) editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
  };

  const clearFormatting = () => {
    editor.update(() => {
      const sel = $getSelection();
      if (!$isRangeSelection(sel)) return;
      (["bold", "italic", "underline", "strikethrough", "subscript", "superscript", "code"] as TextFormatType[]).forEach((f) => {
        if (sel.hasFormat(f)) sel.toggleFormat(f);
      });
      sel.getNodes().forEach((n) => {
        if ((n as any).setStyle) (n as any).setStyle("");
      });
    });
  };

  const doInsertTable = () => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, { rows: String(tableRows), columns: String(tableCols), includeHeaders: false });
    setShowTableDialog(false);
  };

  const doInsertImage = () => {
    if (!imageUrl.trim()) return;
    editor.update(() => {
      const node = $createImageNode(imageUrl.trim(), "Image");
      $insertNodes([node]);
    });
    setShowImageDialog(false);
    setImageUrl("");
  };

  const cur = BLOCK_TYPES[blockType] || BLOCK_TYPES.paragraph;

  const Btn = ({ active, onClick, title, children, disabled }: any) => (
    <button type="button" disabled={disabled} className={`toolbar-item spaced${active ? " active" : ""}`} onClick={onClick} title={title}>
      {children}
    </button>
  );

  return (
    <>
      <div className="lh-toolbar">
        {/* Undo / Redo */}
        <Btn disabled={!canUndo} onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)} title="Undo (Ctrl+Z)">
          {I.undo}
        </Btn>
        <Btn disabled={!canRedo} onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)} title="Redo (Ctrl+Y)">
          {I.redo}
        </Btn>
        <div className="divider" />

        {/* Block Format */}
        <div style={{ position: "relative" }}>
          <button
            type="button"
            ref={blockBtnRef}
            className="toolbar-item block-controls spaced"
            onClick={() => setShowBlockDD((v) => !v)}
            title="Block Format"
          >
            {cur.icon}
            <span className="toolbar-text">{cur.label}</span>
            {I.chevron}
          </button>
          {showBlockDD && (
            <div ref={blockDDRef} className="block-dropdown">
              {Object.entries(BLOCK_TYPES).map(([k, v]) => (
                <button key={k} type="button" onClick={() => formatBlock(k)} className={blockType === k ? "dd-active" : ""}>
                  <span style={{ width: 18, height: 18, display: "flex" }}>{v.icon}</span>
                  <span>{v.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="divider" />

        {/* Font Family */}
        <select className="font-family-select" value={fontFamily} onChange={(e) => setFF(e.target.value)} title="Font Family">
          {FONT_FAMILIES.map((f) => (
            <option key={f} value={f} style={{ fontFamily: f }}>
              {f}
            </option>
          ))}
        </select>

        {/* Font Size */}
        <div className="font-size-controls">
          <button type="button" onClick={() => setFS(String(Math.max(8, parseInt(fontSize) - 1)))} title="Decrease">
            −
          </button>
          <input
            type="text"
            value={fontSize}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "");
              if (v) setFS(v);
            }}
            onBlur={(e) => {
              if (!parseInt(e.target.value) || parseInt(e.target.value) < 8) setFS("15");
            }}
            title="Font Size"
          />
          <button type="button" onClick={() => setFS(String(Math.min(200, parseInt(fontSize) + 1)))} title="Increase">
            +
          </button>
        </div>
        <div className="divider" />

        {/* Text Format */}
        <Btn active={isBold} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")} title="Bold (Ctrl+B)">
          {I.bold}
        </Btn>
        <Btn active={isItalic} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")} title="Italic (Ctrl+I)">
          {I.italic}
        </Btn>
        <Btn active={isUnderline} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")} title="Underline (Ctrl+U)">
          {I.underline}
        </Btn>
        <Btn active={isStrikethrough} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")} title="Strikethrough">
          {I.strikethrough}
        </Btn>
        <Btn active={isSubscript} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "subscript")} title="Subscript">
          {I.subscript}
        </Btn>
        <Btn active={isSuperscript} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "superscript")} title="Superscript">
          {I.superscript}
        </Btn>
        <Btn active={isCode} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code")} title="Inline Code">
          {I.code}
        </Btn>
        <Btn active={isLink} onClick={insertLink} title="Insert Link">
          {I.link}
        </Btn>
        <div className="divider" />

        {/* Font Color */}
        <div className="color-btn-wrap" title="Font Color">
          <button type="button" className="toolbar-item spaced">
            {I.fontColor}
            <span className="color-bar" style={{ background: fontColor }} />
          </button>
          <input type="color" value={fontColor} onChange={(e) => setFC(e.target.value)} />
        </div>

        {/* Background Color */}
        <div className="color-btn-wrap" title="Highlight Color">
          <button type="button" className="toolbar-item spaced">
            {I.bgColor}
            <span className="color-bar" style={{ background: bgColor === "#ffffff" ? "#e0e0e0" : bgColor }} />
          </button>
          <input type="color" value={bgColor} onChange={(e) => setBG(e.target.value)} />
        </div>

        {/* Clear Format */}
        <Btn onClick={clearFormatting} title="Clear Formatting">
          {I.clearFormat}
        </Btn>
        <div className="divider" />

        {/* Alignment */}
        <Btn active={elementFormat === "left"} onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left")} title="Align Left">
          {I.alignLeft}
        </Btn>
        <Btn active={elementFormat === "center"} onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center")} title="Center">
          {I.alignCenter}
        </Btn>
        <Btn active={elementFormat === "right"} onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right")} title="Align Right">
          {I.alignRight}
        </Btn>
        <Btn active={elementFormat === "justify"} onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify")} title="Justify">
          {I.alignJustify}
        </Btn>
        <div className="divider" />

        {/* Indent / Outdent */}
        <Btn onClick={() => editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)} title="Outdent">
          {I.outdent}
        </Btn>
        <Btn onClick={() => editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)} title="Indent">
          {I.indent}
        </Btn>
        <div className="divider" />

        {/* Insert: HR, Table, Image */}
        <Btn onClick={() => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)} title="Horizontal Rule">
          {I.hr}
        </Btn>
        <Btn onClick={() => setShowTableDialog(true)} title="Insert Table">
          {I.table}
        </Btn>
        <Btn onClick={() => setShowImageDialog(true)} title="Insert Image">
          {I.image}
        </Btn>
      </div>

      {/* ── Table Dialog ── */}
      {showTableDialog && (
        <div className="insert-dialog-overlay" onClick={() => setShowTableDialog(false)}>
          <div className="insert-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Insert Table</h3>
            <label>Rows</label>
            <input type="number" min={1} max={50} value={tableRows} onChange={(e) => setTableRows(parseInt(e.target.value) || 1)} />
            <label>Columns</label>
            <input type="number" min={1} max={20} value={tableCols} onChange={(e) => setTableCols(parseInt(e.target.value) || 1)} />
            <div className="insert-dialog-actions">
              <button type="button" onClick={() => setShowTableDialog(false)}>
                Cancel
              </button>
              <button type="button" className="primary" onClick={doInsertTable}>
                Insert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Image Dialog ── */}
      {showImageDialog && (
        <div className="insert-dialog-overlay" onClick={() => setShowImageDialog(false)}>
          <div className="insert-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Insert Image</h3>
            <label>Image URL</label>
            <input
              type="url"
              placeholder="https://..."
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") doInsertImage();
              }}
              autoFocus
            />
            <div className="insert-dialog-actions">
              <button
                type="button"
                onClick={() => {
                  setShowImageDialog(false);
                  setImageUrl("");
                }}
              >
                Cancel
              </button>
              <button type="button" className="primary" onClick={doInsertImage}>
                Insert
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════
// TABLE CELL ACTION MENU — right-click context menu
// ═══════════════════════════════════════════════════════
function getLexicalKeyFromDOM(el: HTMLElement, editor: LexicalEditor): string | null {
  // Lexical stores keys as __lexicalKey_<editorKey> on DOM elements
  const prefix = "__lexicalKey_";
  for (const key of Object.keys(el)) {
    if (key.startsWith(prefix)) return (el as any)[key] as string;
  }
  return null;
}

function TableActionMenuPlugin() {
  const [editor] = useLexicalComposerContext();
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [cellKey, setCellKey] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Clamp menu position so it stays within the viewport
  useEffect(() => {
    if (!menu || !menuRef.current) return;
    const el = menuRef.current;
    const rect = el.getBoundingClientRect();
    let { x, y } = menu;
    if (rect.bottom > window.innerHeight) y = window.innerHeight - rect.height - 8;
    if (rect.right > window.innerWidth) x = window.innerWidth - rect.width - 8;
    if (y < 0) y = 8;
    if (x < 0) x = 8;
    if (x !== menu.x || y !== menu.y) setMenu({ x, y });
  }, [menu]);

  useEffect(() => {
    const root = editor.getRootElement();
    if (!root) return;

    const handleContext = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const td = target.closest("td, th") as HTMLElement | null;
      if (!td || !root.contains(td)) return;
      e.preventDefault();

      // Find the Lexical key on the TD element
      let key = getLexicalKeyFromDOM(td, editor);

      // If no key on the td itself, try to find via selection placement
      if (!key) {
        // Place the native selection at the click point and read the Lexical selection
        const range = document.caretRangeFromPoint(e.clientX, e.clientY);
        if (range) {
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
          // Give Lexical a tick to sync selection, then read
          setTimeout(() => {
            editor.getEditorState().read(() => {
              const lexSel = $getSelection();
              if ($isRangeSelection(lexSel)) {
                const node = lexSel.anchor.getNode();
                const cellNode = $getTableCellNodeFromLexicalNode(node);
                if (cellNode) {
                  setCellKey(cellNode.getKey());
                  setMenu({ x: e.clientX, y: e.clientY });
                }
              }
            });
          }, 0);
        }
        return;
      }

      setCellKey(key);
      setMenu({ x: e.clientX, y: e.clientY });
    };

    root.addEventListener("contextmenu", handleContext);
    return () => root.removeEventListener("contextmenu", handleContext);
  }, [editor]);

  useEffect(() => {
    if (!menu) return;
    const h = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenu(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menu]);

  const close = () => setMenu(null);

  const withCell = (fn: (cellNode: TableCellNode) => void) => {
    editor.update(() => {
      if (!cellKey) return;
      const node = $getNodeByKey(cellKey);
      if (!node) return;
      // node might be the cell itself or a child of a cell
      let cell: TableCellNode | null = null;
      if ($isTableCellNode(node)) {
        cell = node;
      } else {
        cell = $getTableCellNodeFromLexicalNode(node);
      }
      if (cell) {
        // Select into the cell first so *AtSelection helpers work
        const first = cell.getFirstChild();
        if (first) first.selectStart();
        fn(cell);
      }
    });
    close();
  };

  const insertRowAbove = () =>
    withCell(() => {
      $insertTableRowAtSelection(false);
    });
  const insertRowBelow = () =>
    withCell(() => {
      $insertTableRowAtSelection(true);
    });
  const deleteRow = () =>
    withCell(() => {
      $deleteTableRowAtSelection();
    });
  const insertColBefore = () =>
    withCell(() => {
      $insertTableColumnAtSelection(false);
    });
  const insertColAfter = () =>
    withCell(() => {
      $insertTableColumnAtSelection(true);
    });
  const deleteCol = () =>
    withCell(() => {
      $deleteTableColumnAtSelection();
    });

  const deleteTable = () => {
    editor.update(() => {
      if (!cellKey) return;
      const node = $getNodeByKey(cellKey);
      if (!node) return;
      const cell = $isTableCellNode(node) ? node : $getTableCellNodeFromLexicalNode(node);
      if (!cell) return;
      try {
        const table = $getTableNodeFromLexicalNodeOrThrow(cell);
        table.remove();
      } catch {}
    });
    close();
  };

  const setVerticalAlign = (val: string) => {
    editor.update(() => {
      if (!cellKey) return;
      const node = $getNodeByKey(cellKey);
      const cell = node && ($isTableCellNode(node) ? node : $getTableCellNodeFromLexicalNode(node));
      if (cell) cell.setVerticalAlign(val);
    });
    close();
  };

  const setCellBg = (color: string) => {
    editor.update(() => {
      if (!cellKey) return;
      const node = $getNodeByKey(cellKey);
      const cell = node && ($isTableCellNode(node) ? node : $getTableCellNodeFromLexicalNode(node));
      if (cell) cell.setBackgroundColor(color);
    });
  };

  const toggleHeaderRow = () => {
    editor.update(() => {
      if (!cellKey) return;
      const node = $getNodeByKey(cellKey);
      const cell = node && ($isTableCellNode(node) ? node : $getTableCellNodeFromLexicalNode(node));
      if (cell) {
        const cur = cell.getHeaderStyles();
        cell.setHeaderStyles(cur ^ 1);
      }
    });
    close();
  };

  if (!menu) return null;

  return ReactDOM.createPortal(
    <div ref={menuRef} className="table-action-menu mb-2" style={{ top: menu.y, left: menu.x }}>
      <button type="button" onClick={insertRowAbove}>
        Insert Row Above
      </button>
      <button type="button" onClick={insertRowBelow}>
        Insert Row Below
      </button>
      <div className="tam-sep" />
      <button type="button" onClick={insertColBefore}>
        Insert Column Before
      </button>
      <button type="button" onClick={insertColAfter}>
        Insert Column After
      </button>
      <div className="tam-sep" />
      <button type="button" onClick={deleteRow}>
        Delete Row
      </button>
      <button type="button" onClick={deleteCol}>
        Delete Column
      </button>
      <button type="button" onClick={deleteTable} className="tam-danger">
        Delete Table
      </button>
      <div className="tam-sep" />
      <button type="button" onClick={toggleHeaderRow}>
        Toggle Header
      </button>
      <div className="tam-sep" />
      <div className="tam-sub-label">Vertical Align</div>
      <button type="button" onClick={() => setVerticalAlign("top")}>
        ↑ Top
      </button>
      <button type="button" onClick={() => setVerticalAlign("middle")}>
        ⬌ Middle
      </button>
      <button type="button" onClick={() => setVerticalAlign("bottom")}>
        ↓ Bottom
      </button>
      <div className="tam-sep" />
      <div className="tam-color-row">
        <span>Cell Color</span>
        <input type="color" defaultValue="#ffffff" onChange={(e) => setCellBg(e.target.value)} />
      </div>
    </div>,
    document.body,
  );
}

// ═══════════════════════════════════════════════════════
// TABLE CELL RESIZER — drag column/row borders
// ═══════════════════════════════════════════════════════
function TableCellResizerPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const root = editor.getRootElement();
    if (!root) return;

    let dragging = false;
    let startX = 0;
    let startY = 0;
    let targetCell: HTMLTableCellElement | null = null;
    let targetNextCell: HTMLTableCellElement | null = null;
    let targetRow: HTMLTableRowElement | null = null;
    let startW = 0;
    let startNextW = 0;
    let startH = 0;
    let direction: "col" | "row" | null = null;
    const EDGE = 5;

    const getCursorDir = (e: MouseEvent, td: HTMLTableCellElement): "col" | "row" | null => {
      const rect = td.getBoundingClientRect();
      if (Math.abs(e.clientX - rect.right) <= EDGE) return "col";
      if (Math.abs(e.clientX - rect.left) <= EDGE && td.previousElementSibling) return "col";
      if (Math.abs(e.clientY - rect.bottom) <= EDGE) return "row";
      return null;
    };

    const onMove = (e: MouseEvent) => {
      if (dragging) {
        e.preventDefault();
        if (direction === "col" && targetCell) {
          const dx = e.clientX - startX;
          const newW = Math.max(30, startW + dx);
          targetCell.style.width = newW + "px";
          if (targetNextCell) {
            const nextW = Math.max(30, startNextW - dx);
            targetNextCell.style.width = nextW + "px";
          }
        } else if (direction === "row" && targetRow) {
          const dy = e.clientY - startY;
          const newH = Math.max(4, startH + dy);
          targetRow.style.height = newH + "px";
          // Also reduce padding on all cells in the row when shrinking
          const cells = targetRow.querySelectorAll("td, th");
          cells.forEach((c) => {
            (c as HTMLElement).style.paddingTop = newH < 24 ? "1px" : "";
            (c as HTMLElement).style.paddingBottom = newH < 24 ? "1px" : "";
          });
        }
        return;
      }
      // Show cursor
      const target = e.target as HTMLElement;
      const td = target.closest("td, th") as HTMLTableCellElement | null;
      if (td && root.contains(td)) {
        const d = getCursorDir(e, td);
        td.style.cursor = d === "col" ? "col-resize" : d === "row" ? "row-resize" : "";
      }
    };

    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const td = target.closest("td, th") as HTMLTableCellElement | null;
      if (!td || !root.contains(td)) return;
      const d = getCursorDir(e, td);
      if (!d) return;

      e.preventDefault();
      dragging = true;
      direction = d;
      startX = e.clientX;
      startY = e.clientY;

      if (d === "col") {
        // If near left edge, resize the previous cell instead
        const rect = td.getBoundingClientRect();
        if (Math.abs(e.clientX - rect.left) <= EDGE && td.previousElementSibling) {
          targetCell = td.previousElementSibling as HTMLTableCellElement;
          targetNextCell = td;
        } else {
          targetCell = td;
          targetNextCell = td.nextElementSibling as HTMLTableCellElement | null;
        }
        startW = targetCell.offsetWidth;
        startNextW = targetNextCell ? targetNextCell.offsetWidth : 0;
      } else {
        targetRow = td.parentElement as HTMLTableRowElement;
        startH = targetRow.offsetHeight;
      }
    };

    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      // Persist widths to Lexical
      if (direction === "col" && targetCell) {
        const w1 = targetCell.offsetWidth;
        const w2 = targetNextCell ? targetNextCell.offsetWidth : null;
        const key1 = getCellLexicalKey(targetCell);
        const key2 = targetNextCell ? getCellLexicalKey(targetNextCell) : null;
        editor.update(() => {
          if (key1) {
            const n = $getNodeByKey(key1);
            if (n && $isTableCellNode(n)) n.setWidth(w1);
          }
          if (key2) {
            const n = $getNodeByKey(key2);
            if (n && $isTableCellNode(n)) n.setWidth(w2!);
          }
        });
      }
      targetCell = null;
      targetNextCell = null;
      targetRow = null;
      direction = null;
    };

    root.addEventListener("mousemove", onMove);
    root.addEventListener("mousedown", onDown);
    document.addEventListener("mouseup", onUp);
    return () => {
      root.removeEventListener("mousemove", onMove);
      root.removeEventListener("mousedown", onDown);
      document.removeEventListener("mouseup", onUp);
    };
  }, [editor]);

  return null;
}

function getCellLexicalKey(el: HTMLElement): string | null {
  const keys = Object.keys(el).filter((k) => k.startsWith("__lexicalKey"));
  return keys.length > 0 ? (el as any)[keys[0]] : null;
}

// ═══════════════════════════════════════════════════════
// PAGE BREAK SYSTEM — pushes straddling elements to next page
// ═══════════════════════════════════════════════════════
// A4 at 96 DPI: 210mm = 793.7px, 297mm = 1122.5px
const A4_HEIGHT_PX = 1122;
const PAGE_PADDING = 28; // editor-input top/bottom padding
const PAGE_GAP = 40; // visible gap between pages in preview

/** Dynamically compute usable content height per page */
function getPageContentHeight(root: HTMLElement): number {
  const paper = root.closest(".a4-paper");
  const headerH = paper?.querySelector(".letterhead-header")?.getBoundingClientRect().height || 72;
  const footerH = paper?.querySelector(".letterhead-footer")?.getBoundingClientRect().height || 56;
  return A4_HEIGHT_PX - headerH - footerH - PAGE_PADDING * 2;
}

function PageBreakPlugin() {
  const [editor] = useLexicalComposerContext();
  const [pageBreaks, setPageBreaks] = useState<number[]>([]);
  const [pageCount, setPageCount] = useState(1);

  useEffect(() => {
    const root = editor.getRootElement();
    if (!root) return;
    let rafId = 0;
    let isUpdating = false; // guard against MutationObserver re-entry

    const updatePages = () => {
      if (isUpdating) return;
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        isUpdating = true;
        const contentH = getPageContentHeight(root);

        // 1. Clear all previously injected margins
        const children = Array.from(root.children) as HTMLElement[];
        children.forEach((ch) => {
          if (ch.style.marginTop && ch.dataset.pageBreakMargin) {
            ch.style.marginTop = "";
            delete ch.dataset.pageBreakMargin;
          }
        });

        // 2. Compute page breaks & push straddling elements
        // child.offsetTop is relative to the positioned wrapper (position:relative parent),
        // which includes the editor-input's padding-top. So the first child is at ~28px.
        // The page boundary must account for this offset.
        const editorPaddingTop = parseFloat(getComputedStyle(root).paddingTop) || PAGE_PADDING;
        const breaks: number[] = [];
        let pageBottom = editorPaddingTop + contentH; // first page boundary in offsetTop terms
        const fullPageH = contentH + editorPaddingTop; // full page content height for pages 2+

        for (const child of children) {
          // Skip non-element nodes and page break indicators
          if (child.nodeType !== 1 || child.classList.contains("page-break-indicator") || child.classList.contains("page-counter"))
            continue;

          const childTop = child.offsetTop;
          const childBottom = childTop + child.offsetHeight;

          // If this element straddles the page boundary, push it down
          if (childTop < pageBottom && childBottom > pageBottom && childTop !== pageBottom) {
            const pushAmount = pageBottom - childTop + PAGE_GAP;
            child.style.marginTop = pushAmount + "px";
            child.dataset.pageBreakMargin = "1";
            breaks.push(pageBottom);
            pageBottom += fullPageH + pushAmount - PAGE_GAP; // next boundary
          }

          // Check if we've passed the current page boundary entirely
          while (child.offsetTop >= pageBottom) {
            breaks.push(pageBottom);
            pageBottom += fullPageH;
          }
        }

        // Check if total content exceeds current page boundary
        const totalH = root.scrollHeight;
        while (pageBottom < totalH) {
          breaks.push(pageBottom);
          pageBottom += fullPageH;
        }

        setPageBreaks(breaks);
        setPageCount(breaks.length + 1);
        // Allow observer callbacks again after a brief delay
        setTimeout(() => {
          isUpdating = false;
        }, 50);
      });
    };

    const observer = new ResizeObserver(updatePages);
    observer.observe(root);

    const mutObs = new MutationObserver(updatePages);
    mutObs.observe(root, { childList: true, subtree: true, characterData: true, attributes: true });

    updatePages();

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      mutObs.disconnect();
      // Clean up injected margins
      const children = Array.from(root.children) as HTMLElement[];
      children.forEach((ch) => {
        if (ch.dataset.pageBreakMargin) {
          ch.style.marginTop = "";
          delete ch.dataset.pageBreakMargin;
        }
      });
    };
  }, [editor]);

  return (
    <>
      {pageBreaks.map((pos, i) => (
        <div key={i} className="page-break-indicator" style={{ top: pos + PAGE_PADDING }}>
          <div className="page-break-line" />
          <span className="page-break-label">Page {i + 2}</span>
        </div>
      ))}
      <div className="page-counter">Page 1 of {pageCount}</div>
    </>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
const LetterheadEditor: React.FC = () => {
  const printRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const initialConfig = {
    namespace: "LetterheadEditor",
    theme: editorTheme,
    onError: (error: Error) => console.error(error),
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      OverflowNode,
      HorizontalRuleNode,
      ImageNode,
      FloatingImageNode,
    ],
  };

  const handleExportPDF = async () => {
    if (!printRef.current) return;
    setIsExporting(true);
    try {
      const paperEl = printRef.current;
      const headerEl = paperEl.querySelector(".letterhead-header") as HTMLElement;
      const footerEl = paperEl.querySelector(".letterhead-footer") as HTMLElement;
      const editorEl = paperEl.querySelector(".editor-input") as HTMLElement;
      if (!headerEl || !footerEl || !editorEl) return;

      // Hide page-break visuals before capture
      const pageBreakEls = paperEl.querySelectorAll(".page-break-indicator, .page-counter");
      pageBreakEls.forEach((el) => ((el as HTMLElement).style.display = "none"));

      // Build page-slice boundaries from the editor's children.
      // PageBreakPlugin pushes straddling elements down, creating gaps.
      // We build [start, end] pairs that SKIP the gap whitespace.
      const pageRanges: { start: number; end: number }[] = [];
      let currentStart = 0;
      const children = Array.from(editorEl.children) as HTMLElement[];
      for (const child of children) {
        if (child.dataset.pageBreakMargin) {
          const margin = parseFloat(child.style.marginTop) || 0;
          // End of page content = child's original position (before push)
          const pageEnd = child.offsetTop - margin;
          pageRanges.push({ start: currentStart, end: pageEnd });
          // Next page starts at the child's current position (after push, skipping gap)
          currentStart = child.offsetTop;
        }
      }
      // Last page from currentStart to end of content
      // (will be set after canvas is captured)

      // Capture header, footer, and editor content separately
      const SCALE = 2;
      const [headerCanvas, footerCanvas, contentCanvas] = await Promise.all([
        html2canvas(headerEl, { scale: SCALE, useCORS: true, backgroundColor: null }),
        html2canvas(footerEl, { scale: SCALE, useCORS: true, backgroundColor: null }),
        html2canvas(editorEl, { scale: SCALE, useCORS: true, backgroundColor: "#ffffff", windowWidth: editorEl.scrollWidth }),
      ]);

      // Restore page-break visuals
      pageBreakEls.forEach((el) => ((el as HTMLElement).style.display = ""));

      const pdf = new jsPDF("p", "mm", "a4");
      const pW = pdf.internal.pageSize.getWidth(); // 210mm
      const pH = pdf.internal.pageSize.getHeight(); // 297mm

      const px2mm = (px: number) => (px / SCALE) * (25.4 / 96);

      const headerHmm = px2mm(headerCanvas.height);
      const footerHmm = px2mm(footerCanvas.height);

      const headerImg = headerCanvas.toDataURL("image/png");
      const footerImg = footerCanvas.toDataURL("image/png");

      // Content scale factor so content width matches page width
      const contentScaleMM = pW / px2mm(contentCanvas.width);

      // Compute the actual content area height in CSS px from the PDF dimensions
      const contentAreaHmm = pH - headerHmm - footerHmm;
      const contentSliceHCss = contentAreaHmm / contentScaleMM / (25.4 / 96); // mm → CSS px

      // Finalize page ranges: add the last page
      const totalCssH = contentCanvas.height / SCALE;
      if (pageRanges.length > 0) {
        // There were page breaks — add the last page starting from where we left off
        pageRanges.push({ start: currentStart, end: totalCssH });
      } else {
        // No page breaks — single page
        pageRanges.push({ start: 0, end: totalCssH });
      }

      // If no page breaks were found and content exceeds one page, use computed slice height
      if (pageRanges.length === 1 && totalCssH > contentSliceHCss + 10) {
        pageRanges.length = 0;
        let pos = 0;
        while (pos < totalCssH) {
          pageRanges.push({ start: pos, end: Math.min(pos + contentSliceHCss, totalCssH) });
          pos += contentSliceHCss;
        }
      }

      // Slice and render each page
      let pageNum = 0;
      for (const range of pageRanges) {
        const sliceHCss = range.end - range.start;
        if (sliceHCss <= 0) continue;

        if (pageNum > 0) pdf.addPage();

        // Draw header
        pdf.addImage(headerImg, "PNG", 0, 0, pW, headerHmm);

        // Slice content for this page (convert to canvas coords)
        const yPx = range.start * SCALE;
        const hPx = sliceHCss * SCALE;
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = contentCanvas.width;
        sliceCanvas.height = Math.ceil(hPx);
        const ctx = sliceCanvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          ctx.drawImage(contentCanvas, 0, yPx, contentCanvas.width, hPx, 0, 0, contentCanvas.width, Math.ceil(hPx));
          const sliceImg = sliceCanvas.toDataURL("image/png");
          const sliceHmm = px2mm(hPx) * contentScaleMM;
          pdf.addImage(sliceImg, "PNG", 0, headerHmm, pW, sliceHmm);
        }

        // Draw footer at bottom
        pdf.addImage(footerImg, "PNG", 0, pH - footerHmm, pW, footerHmm);

        // Stamp page number on the footer (right-aligned, first line)
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(68, 68, 68);
        pdf.text(`Page ${pageNum + 1} of ${pageRanges.length}`, pW - 4, pH - footerHmm + 5.5, { align: "right" });

        pageNum++;
      }

      // If no content at all, still draw one page
      if (pageNum === 0) {
        pdf.addImage(headerImg, "PNG", 0, 0, pW, headerHmm);
        pdf.addImage(footerImg, "PNG", 0, pH - footerHmm, pW, footerHmm);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(68, 68, 68);
        pdf.text("Page 1 of 1", pW - 4, pH - footerHmm + 5.5, { align: "right" });
      }

      pdf.save("letterhead.pdf");
    } catch (err) {
      console.error("PDF export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const stampImages = [
    { src: "https://api.airfield.express/sign1.png", label: "Anilp Sign" },
    { src: "https://api.airfield.express/stamp1.png", label: "Stamp 1" },
  ];

  return (
    <div className="letterhead-page-wrapper">
      <div className="letterhead-top-bar">
        <h1 style={{ fontSize: 20, fontWeight: 700, color: "#333", margin: 0 }}>Letterhead Editor</h1>
        <button type="button" onClick={handleExportPDF} disabled={isExporting} className="export-pdf-btn">
          {isExporting ? (
            <>
              <svg className="animate-spin" style={{ width: 16, height: 16 }} viewBox="0 0 24 24">
                <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Exporting...
            </>
          ) : (
            <>{I.pdf} Export PDF</>
          )}
        </button>
      </div>

      <div className="letterhead-layout">
        <LexicalComposer initialConfig={initialConfig}>
          <StampPanel stamps={stampImages} />
          <div className="letterhead-main">
            <ToolbarPlugin />
            <div ref={printRef} className="a4-paper" style={{ borderRadius: "0 0 10px 10px" }}>
              {/* Header */}
              <div className="letterhead-header">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <img src="https://api.airfield.express/tuvsud-iso9001.png" alt="Logo" style={{ height: 48 }} crossOrigin="anonymous" />
                  <img src="https://api.airfield.express/logo-main.png" alt="Logo" style={{ height: 48 }} crossOrigin="anonymous" />
                </div>
              </div>

              {/* Editor */}
              <div className="editor-container">
                <div style={{ flex: 1, position: "relative" }}>
                  <RichTextPlugin
                    contentEditable={<ContentEditable className="editor-input" />}
                    placeholder={<div className="editor-placeholder">Start typing your letter here...</div>}
                    ErrorBoundary={LexicalErrorBoundary}
                  />
                  <PageBreakPlugin />
                </div>
              </div>

              {/* Footer */}
              <div className="letterhead-footer" style={{ lineHeight: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#444", fontWeight: 600 }}>
                  <b style={{ color: "navy" }}>Airfield Express Pvt. Ltd.</b>
                  <span className="footer-page-num"></span>
                </div>
                <div style={{ fontSize: 12, color: "navy", marginTop: 3 }}>
                  <b>Head Office:</b> 15/A, Sant Nagar East Of Kailash, New Delhi - 110 065
                </div>
                <div style={{ fontSize: 12, color: "navy", marginTop: 3 }}>
                  Track at: www.airfield.co.in | Email: info@airfield.co.in | Sales: +91 74848 98989 | CIN: U64120DL2012PTC243510
                </div>
                <div style={{ fontSize: 12, color: "navy", marginTop: 3 }}>CS: +91 92272 23131 | CS Email: cs@airfield.co.in</div>
              </div>
            </div>

            <HistoryPlugin />
            <ListPlugin />
            <CheckListPlugin />
            <LinkPlugin />
            <TabIndentationPlugin />
            <ClearEditorPlugin />
            <TablePlugin hasCellMerge hasCellBackgroundColor />
            <HorizontalRulePlugin />
            <TableActionMenuPlugin />
            <TableCellResizerPlugin />
          </div>
        </LexicalComposer>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// STAMP PANEL — left sidebar with clickable stamp images
// ═══════════════════════════════════════════════════════
function StampPanel({ stamps }: { stamps: { src: string; label: string }[] }) {
  const [editor] = useLexicalComposerContext();

  const handleClick = (src: string) => {
    editor.update(() => {
      const node = $createFloatingImageNode(src, 400, 200, 150, 150);
      $insertNodes([node]);
    });
  };

  return (
    <div className="stamp-panel">
      <div className="stamp-panel-title">Stamps</div>
      {stamps.map((s, i) => (
        <div key={i} className="stamp-panel-item" onClick={() => handleClick(s.src)} title={s.label}>
          <img src={s.src} alt={s.label} />
          <span>{s.label}</span>
        </div>
      ))}
      <div className="stamp-panel-title" style={{ marginTop: 12 }}>
        Custom
      </div>
      <button
        className="stamp-panel-add"
        onClick={() => {
          const url = prompt("Enter image URL:");
          if (url?.trim()) handleClick(url.trim());
        }}
      >
        + Add Image
      </button>
    </div>
  );
}

export default LetterheadEditor;
